import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import { cn } from '@/lib/utils';
import type { ApiResponse, DocRecord } from '@shared/types';
import { Loader2 } from 'lucide-react';

export function LibraryPage() {
  const navigate = useNavigate();
  const { data: allDocs, isLoading } = useQuery({
    queryKey: ['docs'],
    queryFn: () => fetch('/api/docs').then(res => res.json() as Promise<ApiResponse<DocRecord[]>>).then(res => res.data || [])
  });

  const docs = allDocs?.filter(d => d.status === 'draft') || [];

  return (
    <AppLayout>
      <div className="flex-1 bg-surface-container-low overflow-y-auto">
        <header className="bg-surface border-b border-outline-variant px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto">
             <span className="font-label-caps text-label-caps text-secondary uppercase tracking-widest mb-2 block">Manuscripts</span>
             <h1 className="font-h1 text-h1 text-on-surface mb-4">Personal Library</h1>
             <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl">
               Your collection of drafted treatises, references, and finalized works. Every inscription is preserved in the Scriptorium's digital vaults.
             </p>
          </div>
        </header>

        <main className="px-4 md:px-margin-page py-12">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-primary opacity-50" />
            </div>
          ) : (
            <div className="max-w-max-width-doc mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
              {docs?.map((doc) => (
                <div 
                  key={doc.slug}
                  onClick={() => navigate(`/doc/${doc.slug}`)}
                  className="group cursor-pointer bg-surface border border-outline-variant rounded-xl p-8 flex flex-col gap-6 transition-all hover:shadow-xl hover:-translate-y-1 active:translate-y-0 active:shadow-md"
                >
                  <div className="flex justify-between items-start">
                    <div className="w-12 h-12 rounded-lg bg-surface-container-highest flex items-center justify-center paper-stack shadow-outline-variant/10 transition-transform group-hover:scale-110">
                      <span className="material-symbols-outlined text-on-surface-variant">book_5</span>
                    </div>
                    <span className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest">
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div>
                    <h3 className="font-h3 text-h3 text-on-surface text-[18px] mb-1 group-hover:text-primary transition-colors leading-tight line-clamp-2">{doc.title}</h3>
                    <p className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-widest mb-4">Archived Manuscript</p>
                    <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed line-clamp-3 italic opacity-60">
                      Press to read the full inscription in the grand reader.
                    </p>
                  </div>

                  <div className="mt-auto flex items-center gap-2 text-primary font-label-caps text-[10px] uppercase tracking-widest pt-4 border-t border-outline-variant/10">
                    <span>Open Manuscript</span>
                    <span className="material-symbols-outlined text-[16px] transition-transform group-hover:translate-x-1">arrow_forward</span>
                  </div>
                </div>
              ))}
              
              <Link 
                to="/" 
                className="flex flex-col items-center justify-center border-2 border-dashed border-outline-variant rounded-xl p-8 hover:bg-surface-container-high hover:border-primary transition-all group gap-4 text-center aspect-[4/5] md:aspect-auto min-h-[300px]"
              >
                <div className="w-16 h-16 rounded-full bg-surface-container-highest flex items-center justify-center shadow-sm border border-outline-variant group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined text-[32px] text-primary">add_notes</span>
                </div>
                <div className="space-y-1">
                  <span className="block font-label-caps text-label-caps text-on-surface uppercase tracking-widest">New Manuscript</span>
                  <p className="font-body-md text-[12px] text-on-surface-variant italic">Start from a blank parchment</p>
                </div>
              </Link>
            </div>
          )}
        </main>
      </div>
    </AppLayout>
  );
}